import { formatDateTimeInTimeZone } from './dates';
import { getPaymentBatchSummary } from './paymentBatch';

const csvEscape = (value) => {
    if (value == null || value === '') return '';
    const str = String(value);
    if (/[",\n\r]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
};

const currencyFormat = (value) => {
    if (value == null || value === '') return '';
    const asNumber = Number(value);
    if (Number.isNaN(asNumber)) return String(value);
    return asNumber.toFixed(2);
};

const LINE_COLUMNS = [
    { key: 'paymentId', label: 'Payment ID' },
    { key: 'organisation', label: 'Organisation', orgOnly: true },
    { key: 'createdAt', label: 'Date' },
    { key: 'paymentMethod', label: 'Payment Method' },
    { key: 'payer', label: 'Payer' },
    { key: 'payee', label: 'Payee' },
    { key: 'reference', label: 'Reference' },
    { key: 'amount', label: 'Amount' },
    { key: 'currency', label: 'Currency' },
    { key: 'status', label: 'Status' },
    { key: 'logs', label: 'Logs' },
];

const BATCH_COLUMNS = [
    { key: 'paymentId', label: 'Payment ID' },
    { key: 'organisation', label: 'Organisation', orgOnly: true },
    { key: 'createdAt', label: 'Date' },
    { key: 'paymentType', label: 'Payment Type' },
    { key: 'totalLines', label: 'Total Lines' },
    { key: 'paymentMethod', label: 'Payment Method' },
    { key: 'payer', label: 'Payer' },
    { key: 'payee', label: 'Payee' },
    { key: 'reference', label: 'Reference' },
    { key: 'amount', label: 'Total Amount' },
    { key: 'currency', label: 'Currency' },
    { key: 'status', label: 'Status' },
    { key: 'logs', label: 'Logs' },
];

const columnsFor = (includeOrganisation, grouping) =>
    (grouping === 'batches' ? BATCH_COLUMNS : LINE_COLUMNS)
        .filter((column) => includeOrganisation || !column.orgOnly);

const combineLogs = (logs) => {
    if (!Array.isArray(logs) || logs.length === 0) return '';
    return logs
        .map((log) => (typeof log === 'string' ? log : log?.message))
        .filter(Boolean)
        .join('\n');
};

const buildBaseRow = (request, includeOrganisation) => ({
    paymentId: request.paymentId || '',
    organisation: includeOrganisation
        ? (request._organisationName || '')
        : undefined,
    paymentMethod: request.paymentMethod || '',
    payer: request.payer?.name || '',
    status: request.status || '',
    logs: combineLogs(request.logs),
});

/**
 * Builds export rows. `grouping: 'lines'` emits one row per payment line;
 * `grouping: 'batches'` emits one row per request, matching the portal list.
 */
export const buildPaymentExportRows = (payments, { timezone, includeOrganisation, grouping = 'lines' } = {}) =>
    (payments || []).flatMap((request) => {
        const lines = request.payments?.length ? request.payments : [{}];
        const base = {
            ...buildBaseRow(request, includeOrganisation),
            createdAt: formatDateTimeInTimeZone(request.createdAt, timezone),
        };

        if (grouping === 'batches') {
            const isBulk = lines.length > 1;
            const { totalAmount, currency, isMultiCurrency, totalsByCurrency } = getPaymentBatchSummary(lines);
            return [{
                ...base,
                paymentType: isBulk ? 'Bulk' : 'Single',
                totalLines: request.totalLines ?? lines.length,
                payee: isBulk ? '[multiple]' : (lines[0].payee?.name || ''),
                reference: isBulk ? '[multiple]' : (lines[0].reference || ''),
                amount: isMultiCurrency
                    ? totalsByCurrency
                        .map((total) => `${total.currency} ${currencyFormat(total.totalAmount)}`)
                        .join('; ')
                    : currencyFormat(totalAmount),
                currency,
            }];
        }

        return lines.map((line) => ({
            ...base,
            payee: line.payee?.name || '',
            reference: line.reference || '',
            amount: currencyFormat(line.amount?.amount),
            currency: line.amount?.currency || '',
        }));
    });

export const paymentsToCsv = (rows, { includeOrganisation, grouping = 'lines' } = {}) => {
    const columns = columnsFor(includeOrganisation, grouping);
    const header = columns.map((column) => csvEscape(column.label)).join(',');
    const body = rows.map((row) =>
        columns.map((column) => csvEscape(row[column.key])).join(',')
    );
    return `\uFEFF${[header, ...body].join('\n')}`;
};

export const paymentsToJson = (rows) => JSON.stringify(rows, null, 2);

export const downloadTextFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const slugifyOrganisationName = (name) => {
    const slug = String(name || 'organisation')
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9._-]+/g, '')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
    return slug || 'organisation';
};

const formatFilenameDateTime = (timeZone) => {
    const date = new Date();
    const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    };

    let parts;
    try {
        parts = new Intl.DateTimeFormat('en-GB', {
            ...(timeZone ? { timeZone } : {}),
            ...options,
        }).formatToParts(date);
    } catch {
        parts = new Intl.DateTimeFormat('en-GB', options).formatToParts(date);
    }

    const get = (type) => parts.find((part) => part.type === type)?.value || '';
    return `${get('year')}-${get('month')}-${get('day')}_${get('hour')}${get('minute')}${get('second')}`;
};

export const buildExportFilename = (format, { organisationName, timezone } = {}) => {
    const extension = format === 'json' ? 'json' : 'csv';
    return `${slugifyOrganisationName(organisationName)}_payments-export_${formatFilenameDateTime(timezone)}.${extension}`;
};
